import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Sparkles, ArrowRight, Loader2, Plane, Crown, PartyPopper } from 'lucide-react';
import { SwoopyWordmark } from '../components/SwoopyLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Simple confetti component without external dependency
function SimpleConfetti() {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    const colors = ['#E9748F', '#FFB6C1', '#FF7B93', '#FFF0F3', '#FFD700'];
    const newParticles = [];
    for (let i = 0; i < 50; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        color: colors[Math.floor(Math.random() * colors.length)],
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2,
        size: 6 + Math.random() * 8
      });
    }
    setParticles(newParticles);
  }, []);
  
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: '-20px',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
          }}
          initial={{ y: 0, opacity: 1, rotate: 0 }}
          animate={{ 
            y: window.innerHeight + 100, 
            opacity: 0,
            rotate: 360 * (Math.random() > 0.5 ? 1 : -1)
          }}
          transition={{ 
            duration: p.duration, 
            delay: p.delay,
            ease: 'linear'
          }}
        />
      ))}
    </div>
  );
}

export default function SuccessPage({ onContinue }) {
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
        const data = await response.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(data);
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 5000);
          return true;
        } else if (data.status === 'expired') {
          setStatus('error');
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount >= 4) {
          setStatus('error');
          return true;
        }
        return false;
      }
    };

    pollPaymentStatus().then(done => {
      if (!done && pollCount < 5) {
        const interval = setInterval(async () => {
          setPollCount(prev => prev + 1);
          const done = await pollPaymentStatus();
          if (done || pollCount >= 5) {
            clearInterval(interval);
          }
        }, 2000);
        return () => clearInterval(interval);
      }
    });
  }, [sessionId, pollCount]);

  const handleContinue = () => {
    window.history.pushState({}, '', '/');
    if (onContinue) onContinue();
  };

  const handleBackToPricing = () => {
    window.history.pushState({}, '', '/pricing');
    window.location.reload();
  };

  const isPlus = paymentData?.package_id === 'plus';

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(180deg, var(--clay-white) 0%, #FFF5F7 50%, var(--clay-white) 100%)'
      }}
    >
      {/* Confetti */}
      {showConfetti && <SimpleConfetti />}

      {/* Background decoration */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 30%, rgba(233, 116, 143, 0.08) 0%, transparent 50%)'
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md w-full relative z-10"
      >
        {/* Logo */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <SwoopyWordmark size="lg" />
        </motion.div>

        {/* Checking State */}
        <AnimatePresence mode="wait">
          {status === 'checking' && (
            <motion.div
              key="checking"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="space-y-6"
            >
              <motion.div 
                className="w-24 h-24 rounded-3xl mx-auto flex items-center justify-center"
                style={{ 
                  background: 'linear-gradient(145deg, var(--swoopy-pink-soft), rgba(233, 116, 143, 0.2))',
                  boxShadow: 'var(--shadow-clay-md)'
                }}
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Loader2 
                  size={44} 
                  className="animate-spin"
                  style={{ color: 'var(--swoopy-pink)' }}
                />
              </motion.div>
              <h1 
                className="text-2xl font-semibold"
                style={{ color: 'var(--text-primary)', fontFamily: "'Fredoka', system-ui" }}
              >
                Mengkonfirmasi pembayaran...
              </h1>
              <p style={{ color: 'var(--text-muted)' }}>
                Mohon tunggu sementara kami memverifikasi pembelianmu
              </p>
            </motion.div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              {/* Success Icon */}
              <motion.div 
                className="relative w-28 h-28 mx-auto"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              >
                <motion.div 
                  className="absolute inset-0 rounded-3xl"
                  style={{ 
                    background: 'linear-gradient(145deg, #FFB6C1 0%, var(--swoopy-pink) 50%, #FF7B93 100%)',
                    boxShadow: '0 20px 40px -10px rgba(233, 116, 143, 0.4)'
                  }}
                  animate={{ 
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.02, 1]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <CheckCircle size={56} color="white" strokeWidth={2} />
                </div>
                
                {/* Floating particles */}
                <motion.div
                  className="absolute -top-2 -right-2"
                  animate={{ y: [0, -5, 0], rotate: [0, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles size={24} style={{ color: 'var(--swoopy-pink)' }} />
                </motion.div>
                <motion.div
                  className="absolute -bottom-1 -left-2"
                  animate={{ y: [0, 5, 0], rotate: [0, -10, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                >
                  <PartyPopper size={20} style={{ color: '#FFD700' }} />
                </motion.div>
              </motion.div>

              <motion.h1 
                className="text-3xl font-semibold"
                style={{ color: 'var(--text-primary)', fontFamily: "'Fredoka', system-ui" }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Pembayaran Berhasil!
              </motion.h1>

              <motion.p 
                className="text-lg"
                style={{ color: 'var(--text-secondary)' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                Selamat datang di <span style={{ color: 'var(--swoopy-pink)', fontWeight: 600 }}>
                  {paymentData?.package_name || 'Swoopy Premium'}
                </span>!
              </motion.p>

              {/* Plan details card */}
              <motion.div 
                className="p-5 rounded-2xl text-left"
                style={{ 
                  backgroundColor: 'white',
                  boxShadow: '0 10px 30px -5px rgba(0, 0, 0, 0.08)',
                  border: '1px solid var(--border-light)'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ 
                      background: 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink))'
                    }}
                  >
                    {isPlus ? (
                      <Crown size={20} color="white" />
                    ) : (
                      <Plane size={20} color="white" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {paymentData?.package_name}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Akses premium aktif
                    </p>
                  </div>
                </div>
                <div 
                  className="text-xs p-3 rounded-xl"
                  style={{ 
                    backgroundColor: 'var(--swoopy-pink-soft)',
                    color: 'var(--swoopy-pink)'
                  }}
                >
                  ✨ Kamu sekarang punya akses ke semua fitur {isPlus ? 'Plus' : 'Basic'}!
                </div>
              </motion.div>

              {/* CTA Button */}
              <motion.button
                onClick={handleContinue}
                className="w-full h-14 rounded-2xl text-base font-semibold mt-6"
                style={{
                  background: 'linear-gradient(145deg, #FFB6C1 0%, var(--swoopy-pink) 30%, #FF7B93 100%)',
                  color: 'white',
                  boxShadow: '0 10px 25px -5px rgba(233, 116, 143, 0.4)',
                  fontFamily: "'Fredoka', system-ui"
                }}
                whileTap={{ scale: 0.98 }}
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                data-testid="success-continue-btn"
              >
                <span className="flex items-center justify-center gap-2">
                  <Sparkles size={20} />
                  Mulai Eksplorasi
                  <ArrowRight size={20} />
                </span>
              </motion.button>
            </motion.div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <div 
                className="w-24 h-24 rounded-3xl mx-auto flex items-center justify-center"
                style={{ 
                  backgroundColor: '#FEE2E2',
                  boxShadow: '0 10px 30px -5px rgba(220, 38, 38, 0.15)'
                }}
              >
                <span className="text-5xl">😕</span>
              </div>
              <h1 
                className="text-2xl font-semibold"
                style={{ color: 'var(--text-primary)', fontFamily: "'Fredoka', system-ui" }}
              >
                Terjadi Kesalahan
              </h1>
              <p style={{ color: 'var(--text-muted)' }}>
                Kami tidak bisa mengkonfirmasi pembayaranmu. Silakan coba lagi.
              </p>
              <motion.button
                onClick={handleBackToPricing}
                className="w-full h-12 rounded-xl font-medium"
                style={{
                  backgroundColor: 'white',
                  color: 'var(--swoopy-pink)',
                  border: '2px solid var(--swoopy-pink)'
                }}
                whileTap={{ scale: 0.98 }}
              >
                Kembali ke Harga
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
